import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { of, switchMap } from 'rxjs';
import { UserApiService } from '../../../core/http/user-api.service';
import { NotificationService } from '../../../core/services/notification.service';
import { AppUser } from '../../../core/authorization/models';

/**
 * Typed form interface for user profile editing.
 */
interface ProfileForm {
  userId: FormControl<number | null>;
  userTypeId: FormControl<number | null>;
  firstName: FormControl<string | null>;
  lastName: FormControl<string | null>;
  email: FormControl<string | null>;
  workPhone: FormControl<string | null>;
}

/**
 * Dialog component for editing user profile information.
 * Allows users to update their email, name, and contact details.
 */
@Component({
  selector: 'app-profile',
  templateUrl: './profile.component.html',
  styleUrl: './profile.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
  ],
})
export class ProfileComponent implements OnInit {
  private readonly userApi = inject(UserApiService);
  private readonly fb = inject(FormBuilder);
  private readonly dialogRef = inject(MatDialogRef<ProfileComponent>);
  private readonly notificationService = inject(NotificationService);
  private readonly destroyRef = inject(DestroyRef);

  private user: AppUser | null = null;
  profileForm!: FormGroup<ProfileForm>;

  ngOnInit(): void {
    this.initializeForm();
  }

  private initializeForm(): void {
    this.profileForm = this.fb.group<ProfileForm>({
      userId: this.fb.control(null),
      userTypeId: this.fb.control(null),
      firstName: this.fb.control(null, [Validators.required, Validators.maxLength(50)]),
      lastName: this.fb.control(null, [Validators.required, Validators.maxLength(50)]),
      email: this.fb.control(null, {
        updateOn: 'blur',
        validators: [Validators.email, Validators.required],
      }),
      workPhone: this.fb.control(null),
    });

    this.userApi
      .getUser(0)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((user) => {
        this.user = user;
        this.patchForm();
      });

    this.profileForm.controls.email.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap((email: string | null) => {
          if (this.profileForm.controls.email.valid && email) {
            return this.userApi.validateEmail(email);
          }
          return of(null);
        }),
      )
      .subscribe((userId) => {
        if (userId && this.user && userId !== this.user.appUserId) {
          this.notificationService.showClientError(
            'This email is associated with a different account.',
          );
          // Use emitEvent: false to prevent potential loop from setValue triggering valueChanges
          this.profileForm.controls.email.setValue(this.user.email, { emitEvent: false });
        }
      });
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  save(): void {
    if (!this.user) return;

    const formValue = this.profileForm.getRawValue();
    const updatedUser: AppUser = {
      ...this.user,
      email: formValue.email ?? '',
      firstName: formValue.firstName ?? '',
      lastName: formValue.lastName ?? '',
      workPhone: formValue.workPhone || null,
    };
    this.userApi
      .save(updatedUser)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.notificationService.showSuccess('Profile has been updated');
        this.profileForm.markAsUntouched();
        this.profileForm.markAsPristine();
      });
  }

  private patchForm(): void {
    if (this.user) {
      this.profileForm.patchValue(this.user);
      this.profileForm.markAsUntouched();
      this.profileForm.markAsPristine();
    }
  }
}
